import type { UserGroup } from '@gmn-portal/backend';
import {
  AlertTriangle,
  Calendar,
  Check,
  CircleDot,
  Contact,
  Fuel,
  Hash,
  Lightbulb,
  Link2,
  MapPin,
  Phone,
  Plane,
  Plus,
  SeparatorHorizontal,
  Stethoscope,
  ToggleLeft,
  Trash2,
  TriangleAlert,
  Type,
} from 'lucide-react';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useState } from 'react';
import { useObjectCategories } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import {
  DEFAULT_CUSTOMER_GROUP_KEYS,
  DEFAULT_GMN_GROUP_KEYS,
  HELI_PROVIDER_GROUP_KEY,
  sortUserGroupsByKey,
} from '@/shared/utils/permissions';
import type {
  FieldPermission,
  FieldPermissions,
  FieldType,
  FieldWidth,
  MetadataField,
  MetadataGroup,
  StatusOption,
} from './types';

interface MetadataFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field?: MetadataField | null;
  fields: MetadataField[];
  groups: MetadataGroup[];
  onSave: (field: Partial<MetadataField>) => void;
  userGroups?: UserGroup[];
  defaultUpdateGroupKeys?: string[];
}

const fieldTypeOptions: {
  value: FieldType;
  icon: React.ComponentType<{ className?: string }>;
  descriptionKey: string;
}[] = [
  {
    value: 'string',
    icon: Type,
    descriptionKey: 'fieldTypeDescriptions.string',
  },
  {
    value: 'number',
    icon: Hash,
    descriptionKey: 'fieldTypeDescriptions.number',
  },
  {
    value: 'boolean',
    icon: ToggleLeft,
    descriptionKey: 'fieldTypeDescriptions.boolean',
  },
  {
    value: 'date',
    icon: Calendar,
    descriptionKey: 'fieldTypeDescriptions.date',
  },
  {
    value: 'contact',
    icon: Contact,
    descriptionKey: 'fieldTypeDescriptions.contact',
  },
  {
    value: 'coordinate',
    icon: MapPin,
    descriptionKey: 'fieldTypeDescriptions.coordinate',
  },
  {
    value: 'positions',
    icon: MapPin,
    descriptionKey: 'fieldTypeDescriptions.positions',
  },
  {
    value: 'phoneNumbers',
    icon: Phone,
    descriptionKey: 'fieldTypeDescriptions.phoneNumbers',
  },
  {
    value: 'status',
    icon: CircleDot,
    descriptionKey: 'fieldTypeDescriptions.status',
  },
  {
    value: 'section',
    icon: SeparatorHorizontal,
    descriptionKey: 'fieldTypeDescriptions.section',
  },
  {
    value: 'relation-display',
    icon: Link2,
    descriptionKey: 'fieldTypeDescriptions.relation-display',
  },
];

// Status field icon options - 'none' used instead of '' because Radix Select doesn't allow empty strings
const statusIconOptions = [
  { value: 'none', icon: null, labelKey: 'metadata.status.noIcon' },
  { value: 'fuel', icon: Fuel, labelKey: 'metadata.status.icons.fuel' },
  {
    value: 'helideck',
    icon: Plane,
    labelKey: 'metadata.status.icons.helideck',
  },
  {
    value: 'warning',
    icon: TriangleAlert,
    labelKey: 'metadata.status.icons.warning',
  },
  { value: 'check', icon: Check, labelKey: 'metadata.status.icons.check' },
  {
    value: 'lightning',
    icon: Lightbulb,
    labelKey: 'metadata.status.icons.lightning',
  },
  {
    value: 'medical',
    icon: Stethoscope,
    labelKey: 'metadata.status.icons.medical',
  },
] as const;

type StatusIcon = (typeof statusIconOptions)[number]['value'];

interface StatusVariantFormItem {
  id: string;
  labelDe: string;
  labelEn: string;
}

type GroupMeta = {
  key: string;
  domain: 'gmn' | 'customer';
};

const statusColorTextClasses: Record<'green' | 'yellow' | 'red', string> = {
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
};

const normalizePermissions = (
  availableGroups: GroupMeta[],
  permissions: FieldPermissions
): FieldPermissions => {
  const mergedKeys = new Set([
    ...availableGroups.map((group) => group.key),
    ...Object.keys(permissions ?? {}),
  ]);
  const normalized: FieldPermissions = {};

  mergedKeys.forEach((key) => {
    if (key === 'gmn-admin') {
      normalized[key] = { read: true, update: true };
      return;
    }
    const entry = permissions?.[key];
    normalized[key] = {
      read: Boolean(entry?.read),
      update: Boolean(entry?.update),
    };
  });

  return normalized;
};

const buildDefaultPermissions = (
  availableGroups: GroupMeta[],
  defaultUpdateGroupKeys: string[]
): FieldPermissions => {
  const editableGroupKeys = new Set(['gmn-admin', ...defaultUpdateGroupKeys]);

  return availableGroups.reduce<FieldPermissions>((acc, group) => {
    acc[group.key] = {
      read: true,
      update: editableGroupKeys.has(group.key),
    };
    return acc;
  }, {});
};

const compactPermissions = (permissions: FieldPermissions): FieldPermissions => {
  const compact: FieldPermissions = {};
  Object.entries(permissions).forEach(([key, value]) => {
    if (key === 'gmn-admin') return;
    if (value.read || value.update) {
      compact[key] = { read: value.read, update: value.update };
    }
  });
  return compact;
};

const EMPTY_DEFAULT_UPDATE_GROUP_KEYS: string[] = [];

export function MetadataFieldDialog({
  open,
  onOpenChange,
  field,
  fields,
  groups,
  onSave,
  userGroups,
  defaultUpdateGroupKeys = EMPTY_DEFAULT_UPDATE_GROUP_KEYS,
}: MetadataFieldDialogProps) {
  const { t, locale } = useIntl();
  const isEditing = !!field?.key && !field.key.startsWith('temp-');

  const [type, setType] = useState<FieldType>('string');
  const [labelDe, setLabelDe] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [permissions, setPermissions] = useState<FieldPermissions>({});
  const [width, setWidth] = useState<FieldWidth>('50');
  const [required, setRequired] = useState(false);
  const [statusLabel, setStatusLabel] = useState('');
  const [statusLabelEn, setStatusLabelEn] = useState('');
  const [groupId, setGroupId] = useState('');

  // Status field specific state
  const [enableYellow, setEnableYellow] = useState(false);
  const [greenVariants, setGreenVariants] = useState<StatusVariantFormItem[]>([]);
  const [yellowVariants, setYellowVariants] = useState<StatusVariantFormItem[]>([]);
  const [redVariants, setRedVariants] = useState<StatusVariantFormItem[]>([]);
  const [isGroupIndicator, setIsGroupIndicator] = useState(false);
  const [statusIcon, setStatusIcon] = useState<StatusIcon>('none');
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const [enumOptions, setEnumOptions] = useState<string[]>([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [multiline, setMultiline] = useState(false);
  const [relationDisplayVariant, setRelationDisplayVariant] = useState<
    'ship-linked-windpark-helicopters' | 'windpark-linked-ship-medics'
  >('ship-linked-windpark-helicopters');
  const [sourceContactFieldKeys, setSourceContactFieldKeys] = useState<string[]>([]);
  const [pendingSourceContactFieldKey, setPendingSourceContactFieldKey] = useState<string>('');

  const createEmptyStatusVariant = (): StatusVariantFormItem => ({
    id: `status-opt-${nanoid(8)}`,
    labelDe: '',
    labelEn: '',
  });

  const mapStatusOptionsToVariants = (
    statusOpts: StatusOption[],
    color: 'green' | 'yellow' | 'red'
  ): StatusVariantFormItem[] => {
    const variants = statusOpts
      .filter((o) => o.color === color)
      .map((o, index) => ({
        id: o.id?.trim() || `status-opt-${color}-${index + 1}`,
        labelDe: o.labelDe ?? '',
        labelEn: o.labelEn ?? '',
      }));

    if (variants.length > 0) {
      return variants;
    }

    return [createEmptyStatusVariant()];
  };

  const resolvedGroups = useMemo<GroupMeta[]>(() => {
    if (userGroups && userGroups.length > 0) {
      const orderedGroups = sortUserGroupsByKey(userGroups);
      return orderedGroups.map((group) => ({
        key: group.key,
        domain: group.domain as GroupMeta['domain'],
      }));
    }
    return [
      ...DEFAULT_GMN_GROUP_KEYS.map((key) => ({ key, domain: 'gmn' as const })),
      ...DEFAULT_CUSTOMER_GROUP_KEYS.map((key) => ({ key, domain: 'customer' as const })),
    ];
  }, [userGroups]);

  const gmnGroups = resolvedGroups.filter((group) => group.domain === 'gmn');
  const heliProviderGroups = resolvedGroups.filter(
    (group) => group.key === HELI_PROVIDER_GROUP_KEY
  );
  const customerGroups = resolvedGroups.filter(
    (group) => group.domain === 'customer' && group.key !== HELI_PROVIDER_GROUP_KEY
  );

  const { data: shipCategoryOptions } = useObjectCategories({
    where: {
      or: [
        { name: { equals: 'Schiff' } },
        { name: { equals: 'Jack-Up-Schiff' } },
        { name: { equals: 'Jack Up Schiff' } },
        { name: { equals: 'Jack-Up' } },
      ],
    },
    limit: 200,
    disabled:
      !open ||
      type !== 'relation-display' ||
      relationDisplayVariant !== 'windpark-linked-ship-medics',
  });

  const availableSourceContactFields = useMemo(() => {
    const fieldMap = new Map<string, { key: string; label: string }>();

    for (const category of shipCategoryOptions?.docs || []) {
      const metadataSchema = category.metadataSchema as
        | {
            properties?: Record<
              string,
              {
                type?: string;
                description?: string;
                description_en?: string;
              }
            >;
          }
        | undefined;

      for (const [key, value] of Object.entries(metadataSchema?.properties || {})) {
        if (value?.type !== 'contact' || fieldMap.has(key)) continue;
        const localizedLabel =
          locale === 'en-US' && value.description_en?.trim()
            ? value.description_en
            : value.description || key;
        fieldMap.set(key, {
          key,
          label: localizedLabel,
        });
      }
    }

    return Array.from(fieldMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [locale, shipCategoryOptions?.docs]);

  // Reset form when field changes
  useEffect(() => {
    if (field) {
      const nextGroupId = groups.some((group) => group.id === field.groupId)
        ? field.groupId
        : groups[0]?.id || '';
      const isTempField = field.key?.startsWith('temp-');
      const basePermissions = isTempField
        ? buildDefaultPermissions(resolvedGroups, defaultUpdateGroupKeys)
        : field.permissions;
      setType(field.type);
      setLabelDe(field.labelDe);
      setLabelEn(field.labelEn);
      setPermissions(normalizePermissions(resolvedGroups, basePermissions));
      setWidth(field.width);
      setRequired(field.required);
      setStatusLabel(field.statusLabel ?? '');
      setStatusLabelEn(field.statusLabelEn ?? '');
      setFilterFavorite(field.filterFavorite ?? false);
      setHighlighted(field.highlighted ?? false);
      setEnumOptions(field.enum ?? []);
      setIsSelectMode((field.enum?.length ?? 0) > 0);
      setMultiline(field.multiline ?? false);
      setGroupId(nextGroupId);
      setRelationDisplayVariant(
        (field.relationDisplayVariant as
          | 'ship-linked-windpark-helicopters'
          | 'windpark-linked-ship-medics'
          | undefined) ?? 'ship-linked-windpark-helicopters'
      );
      setSourceContactFieldKeys(field.sourceContactFieldKeys ?? []);
      setPendingSourceContactFieldKey(field.sourceContactFieldKeys?.[0] ?? '');

      // Status field specific
      const statusOpts = field.statusOptions ?? [];
      const green = mapStatusOptionsToVariants(statusOpts, 'green');
      const yellow = statusOpts.some((o) => o.color === 'yellow')
        ? mapStatusOptionsToVariants(statusOpts, 'yellow')
        : [createEmptyStatusVariant()];
      const red = mapStatusOptionsToVariants(statusOpts, 'red');

      setGreenVariants(green);
      setYellowVariants(yellow);
      setRedVariants(red);
      setEnableYellow(statusOpts.some((o) => o.color === 'yellow'));
      setIsGroupIndicator(field.isGroupIndicator ?? false);
      setStatusIcon((field.icon as StatusIcon) ?? 'none');
    } else {
      setType('string');
      setLabelDe('');
      setLabelEn('');
      setPermissions(
        normalizePermissions(
          resolvedGroups,
          buildDefaultPermissions(resolvedGroups, defaultUpdateGroupKeys)
        )
      );
      setWidth('50');
      setRequired(false);
      setStatusLabel('');
      setStatusLabelEn('');

      // Reset status fields
      setEnableYellow(false);
      setGreenVariants([createEmptyStatusVariant()]);
      setYellowVariants([createEmptyStatusVariant()]);
      setRedVariants([createEmptyStatusVariant()]);
      setIsGroupIndicator(false);
      setStatusIcon('none');
      setFilterFavorite(false);
      setHighlighted(false);
      setEnumOptions([]);
      setIsSelectMode(false);
      setMultiline(false);
      setGroupId(groups[0]?.id || '');
      setRelationDisplayVariant('ship-linked-windpark-helicopters');
      setSourceContactFieldKeys([]);
      setPendingSourceContactFieldKey('');
    }
  }, [defaultUpdateGroupKeys, field, groups, open, resolvedGroups]);

  useEffect(() => {
    if (availableSourceContactFields.length === 0) {
      if (pendingSourceContactFieldKey) {
        setPendingSourceContactFieldKey('');
      }
      return;
    }

    const selectedStillExists = availableSourceContactFields.some(
      (entry) => entry.key === pendingSourceContactFieldKey
    );
    if (!selectedStillExists) {
      setPendingSourceContactFieldKey(availableSourceContactFields[0]?.key ?? '');
    }
  }, [availableSourceContactFields, pendingSourceContactFieldKey]);

  const existingGroupIndicatorName =
    fields.find(
      (f) =>
        f.groupId === groupId &&
        f.type === 'status' &&
        f.isGroupIndicator === true &&
        f.id !== field?.id
    )?.labelDe ?? null;
  const isGroupIndicatorDisabled = !!existingGroupIndicatorName;

  useEffect(() => {
    if (isGroupIndicatorDisabled && isGroupIndicator) {
      setIsGroupIndicator(false);
    }
  }, [isGroupIndicator, isGroupIndicatorDisabled]);

  const handlePermissionChange = (key: string, next: Partial<FieldPermission>) => {
    setPermissions((prev) => {
      const current = prev[key] ?? { read: false, update: false };
      const merged = { ...current, ...next };
      if (!merged.read) {
        merged.update = false;
      }
      if (merged.update) {
        merged.read = true;
      }
      return { ...prev, [key]: merged };
    });
  };

  const handleSave = () => {
    if (!labelDe.trim()) {
      return;
    }

    const normalizedEnumOptions = Array.from(
      new Set(enumOptions.map((option) => option.trim()).filter((option) => option.length > 0))
    );

    const resolvedGroupId = groupId || field?.groupId || groups[0]?.id || '';
    const isFullWidthType =
      type === 'section' ||
      type === 'contact' ||
      type === 'coordinate' ||
      type === 'positions' ||
      type === 'phoneNumbers' ||
      type === 'status' ||
      type === 'relation-display';

    // Build status options if status field
    let statusOptions: StatusOption[] | undefined;
    if (type === 'status') {
      const buildColorOptions = (
        color: 'green' | 'yellow' | 'red',
        variants: StatusVariantFormItem[],
        defaults: { de: string; en: string }
      ): StatusOption[] => {
        const mapped = variants
          .map((variant) => {
            const labelDe = variant.labelDe.trim();
            const labelEn = variant.labelEn.trim();

            if (!labelDe && !labelEn) {
              return null;
            }

            return {
              id: variant.id,
              color,
              labelDe: labelDe || defaults.de,
              labelEn: labelEn || labelDe || defaults.en,
            } satisfies StatusOption;
          })
          .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

        if (mapped.length > 0) {
          return mapped;
        }

        return [
          {
            id: `status-opt-${nanoid(8)}`,
            color,
            labelDe: defaults.de,
            labelEn: defaults.en,
          },
        ];
      };

      const greenOptions = buildColorOptions('green', greenVariants, {
        de: 'Aktiv',
        en: 'Active',
      });
      const yellowOptions = enableYellow
        ? buildColorOptions('yellow', yellowVariants, {
            de: 'Warnung',
            en: 'Warning',
          })
        : [];
      const redOptions = buildColorOptions('red', redVariants, {
        de: 'Inaktiv',
        en: 'Inactive',
      });

      statusOptions = [...greenOptions, ...yellowOptions, ...redOptions];
    }

    onSave({
      key: field?.key,
      labelDe: labelDe.trim(),
      labelEn: labelEn.trim(),
      type,
      required,
      permissions: compactPermissions(permissions),
      filterFavorite,
      highlighted,
      groupId: resolvedGroupId,
      width: isFullWidthType ? '100' : width,
      ...(type === 'string' &&
        isSelectMode &&
        normalizedEnumOptions.length > 0 && { enum: normalizedEnumOptions }),
      ...(type === 'string' && !isSelectMode && multiline && { multiline: true }),
      ...(type === 'positions' && { statusLabel: statusLabel.trim() }),
      ...(type === 'positions' && { statusLabelEn: statusLabelEn.trim() }),
      ...(type === 'status' && { statusOptions }),
      ...(type === 'status' && { isGroupIndicator }),
      ...(type === 'status' && { icon: statusIcon === 'none' ? undefined : statusIcon }),
      ...(type === 'relation-display' && { relationDisplayVariant }),
      ...(type === 'relation-display' &&
        relationDisplayVariant === 'windpark-linked-ship-medics' &&
        sourceContactFieldKeys.length > 0 && { sourceContactFieldKeys }),
    });

    onOpenChange(false);
  };

  const updateVariant = (
    color: 'green' | 'yellow' | 'red',
    id: string,
    updates: Partial<StatusVariantFormItem>
  ) => {
    const updater = (prev: StatusVariantFormItem[]) =>
      prev.map((variant) => (variant.id === id ? { ...variant, ...updates } : variant));

    if (color === 'green') {
      setGreenVariants(updater);
      return;
    }
    if (color === 'yellow') {
      setYellowVariants(updater);
      return;
    }
    setRedVariants(updater);
  };

  const addVariant = (color: 'green' | 'yellow' | 'red') => {
    const newVariant = createEmptyStatusVariant();
    if (color === 'green') {
      setGreenVariants((prev) => [...prev, newVariant]);
      return;
    }
    if (color === 'yellow') {
      setYellowVariants((prev) => [...prev, newVariant]);
      return;
    }
    setRedVariants((prev) => [...prev, newVariant]);
  };

  const removeVariant = (color: 'green' | 'yellow' | 'red', id: string) => {
    const updater = (prev: StatusVariantFormItem[]) => {
      const next = prev.filter((variant) => variant.id !== id);
      return next.length > 0 ? next : [createEmptyStatusVariant()];
    };

    if (color === 'green') {
      setGreenVariants(updater);
      return;
    }
    if (color === 'yellow') {
      setYellowVariants(updater);
      return;
    }
    setRedVariants(updater);
  };

  const addEnumOption = () => {
    setEnumOptions((prev) => [...prev, '']);
  };

  const updateEnumOption = (index: number, value: string) => {
    setEnumOptions((prev) =>
      prev.map((option, optionIndex) => (optionIndex === index ? value : option))
    );
  };

  const removeEnumOption = (index: number) => {
    setEnumOptions((prev) => prev.filter((_, optionIndex) => optionIndex !== index));
  };

  const handleSelectModeChange = (checked: boolean) => {
    setIsSelectMode(checked);
    if (checked && enumOptions.length === 0) {
      setEnumOptions(['']);
    }
  };

  const addSourceContactFieldKey = () => {
    if (!pendingSourceContactFieldKey) return;
    setSourceContactFieldKeys((prev) =>
      prev.includes(pendingSourceContactFieldKey) ? prev : [...prev, pendingSourceContactFieldKey]
    );
  };

  const removeSourceContactFieldKey = (contactFieldKey: string) => {
    setSourceContactFieldKeys((prev) => prev.filter((entry) => entry !== contactFieldKey));
  };

  const renderColorVariants = (
    color: 'green' | 'yellow' | 'red',
    labelKey: 'greenLabel' | 'yellowLabel' | 'redLabel',
    variants: StatusVariantFormItem[]
  ) => {
    const labelBase = t(`metadata.status.${labelKey}`);
    const placeholder = t(`metadata.status.${labelKey}Placeholder`);

    return (
      <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CircleDot className={cn('size-4', statusColorTextClasses[color])} />
            <span className="text-sm font-semibold">{labelBase}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addVariant(color)}
            data-testid={`metadata-status-${color}-add-variant`}
          >
            <Plus className="size-4" />
            {t('metadata.status.addVariant')}
          </Button>
        </div>

        {variants.map((variant, index) => (
          <div
            key={variant.id}
            className="space-y-2 rounded-md border border-border bg-background p-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                {t('metadata.status.variantIndex', { index: index + 1 })}
              </span>
              {variants.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => removeVariant(color, variant.id)}
                  data-testid={`metadata-status-${color}-remove-variant-${index}`}
                  aria-label={t('common.remove')}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor={`metadata-status-${color}-label-de-${variant.id}`}>
                  {`${labelBase} (DE)`}
                  {index > 0 ? ` (${t('common.optional')})` : ''}
                </Label>
                <Input
                  id={`metadata-status-${color}-label-de-${variant.id}`}
                  value={variant.labelDe}
                  onChange={(e) => updateVariant(color, variant.id, { labelDe: e.target.value })}
                  placeholder={placeholder}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`metadata-status-${color}-label-en-${variant.id}`}>
                  {`${labelBase} (EN)`}
                  {index > 0 ? ` (${t('common.optional')})` : ''}
                </Label>
                <Input
                  id={`metadata-status-${color}-label-en-${variant.id}`}
                  value={variant.labelEn}
                  onChange={(e) => updateVariant(color, variant.id, { labelEn: e.target.value })}
                  placeholder={placeholder}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPermissionTable = (title: string, groupsToRender: GroupMeta[]) => {
    if (groupsToRender.length === 0) return null;

    return (
      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </span>
          <div className="grid grid-cols-[80px_80px] gap-2 text-xs text-muted-foreground">
            <span className="text-center">{t('metadata.permissions.read')}</span>
            <span className="text-center">{t('metadata.permissions.update')}</span>
          </div>
        </div>
        <div className="divide-y">
          {groupsToRender.map((group) => {
            const entry = permissions[group.key] ?? { read: false, update: false };
            const isGmnAdmin = group.key === 'gmn-admin';
            const labelKey = `portalRoles.${group.key}`;
            const label = t(labelKey as unknown as never);
            const displayLabel = label === labelKey ? group.key : label;
            return (
              <div
                key={group.key}
                className="grid grid-cols-[1fr_80px_80px] items-center gap-2 px-3 py-2 text-sm"
              >
                <span className={cn('truncate', isGmnAdmin && 'text-muted-foreground')}>
                  {displayLabel}
                </span>
                <div className="flex justify-center">
                  <Checkbox
                    checked={entry.read}
                    onCheckedChange={(checked) =>
                      handlePermissionChange(group.key, { read: Boolean(checked) })
                    }
                    disabled={isGmnAdmin}
                  />
                </div>
                <div className="flex justify-center">
                  <Checkbox
                    checked={entry.update}
                    onCheckedChange={(checked) =>
                      handlePermissionChange(group.key, { update: Boolean(checked) })
                    }
                    disabled={isGmnAdmin}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[960px]! max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('metadata.builder.editField') : t('metadata.builder.createField')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('metadata.builder.editFieldDescription')
              : t('metadata.builder.createFieldDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Field Type Selector */}
          {!isEditing && (
            <div className="space-y-2">
              <Label>{t('metadata.fieldType')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {fieldTypeOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setType(option.value)}
                      className={cn(
                        'flex items-center gap-2 p-3 rounded-lg border text-left transition-colors',
                        type === option.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border hover:border-primary/50 hover:bg-muted/40'
                      )}
                    >
                      <Icon className="size-4" />
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium">
                          {t(`metadata.fieldTypes.${option.value}`)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t(`metadata.${option.descriptionKey}`)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Field Labels */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="metadata-field-name-de">{`${t('metadata.fieldName')} (DE)`}</Label>
              <Input
                id="metadata-field-name-de"
                value={labelDe}
                onChange={(e) => setLabelDe(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="metadata-field-name-en">{`${t('metadata.fieldName')} (EN)`}</Label>
              <Input
                id="metadata-field-name-en"
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
              />
            </div>
          </div>

          {/* Group and Width */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-2">
              <Label>{t('metadata.groups.group')}</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger data-testid="metadata-field-group-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('metadata.builder.width')}</Label>
              <Select
                value={width}
                onValueChange={(value) => setWidth(value as FieldWidth)}
                disabled={
                  type === 'section' ||
                  type === 'contact' ||
                  type === 'coordinate' ||
                  type === 'positions' ||
                  type === 'phoneNumbers' ||
                  type === 'status' ||
                  type === 'relation-display'
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25%</SelectItem>
                  <SelectItem value="33">33%</SelectItem>
                  <SelectItem value="50">50%</SelectItem>
                  <SelectItem value="100">100%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Required + Favorites */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="metadata-required"
                checked={required}
                onCheckedChange={(checked) => setRequired(Boolean(checked))}
                disabled={[
                  'section',
                  'contact',
                  'coordinate',
                  'positions',
                  'phoneNumbers',
                  'status',
                  'relation-display',
                  'boolean',
                ].includes(type)}
              />
              <Label htmlFor="metadata-required">{t('metadata.required')}</Label>
            </div>
            {['string', 'number', 'date', 'boolean', 'status'].includes(type) && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="metadata-filter-favorite"
                  checked={filterFavorite}
                  onCheckedChange={(checked) => setFilterFavorite(Boolean(checked))}
                  data-testid="metadata-field-filter-favorite"
                />
                <Label htmlFor="metadata-filter-favorite">
                  {t('metadata.builder.filterFavorite')}
                </Label>
              </div>
            )}
            {type !== 'section' && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="metadata-highlighted"
                  checked={highlighted}
                  onCheckedChange={(checked) => setHighlighted(Boolean(checked))}
                  data-testid="metadata-field-highlighted"
                />
                <Label htmlFor="metadata-highlighted">{t('metadata.builder.highlighted')}</Label>
              </div>
            )}
            {type === 'string' && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="metadata-enable-select-mode"
                  checked={isSelectMode}
                  onCheckedChange={(checked) => handleSelectModeChange(Boolean(checked))}
                />
                <Label htmlFor="metadata-enable-select-mode">{t('metadata.select.enable')}</Label>
              </div>
            )}
            {type === 'string' && !isSelectMode && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="metadata-multiline"
                  checked={multiline}
                  onCheckedChange={(checked) => setMultiline(Boolean(checked))}
                  data-testid="metadata-field-multiline"
                />
                <Label htmlFor="metadata-multiline">{t('metadata.builder.multiline')}</Label>
              </div>
            )}
          </div>

          {/* String Enum (Select) Options */}
          {type === 'string' && isSelectMode && (
            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-1">
                  <Label>{t('metadata.select.title')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('metadata.select.description')}
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addEnumOption}>
                  <Plus className="size-4" />
                  {t('metadata.select.addOption')}
                </Button>
              </div>

              {enumOptions.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('metadata.select.noOptions')}</p>
              )}

              {enumOptions.map((option, index) => (
                <div key={`enum-option-${index}`} className="flex items-center gap-2">
                  <Input
                    value={option}
                    onChange={(event) => updateEnumOption(index, event.target.value)}
                    placeholder={t('metadata.select.optionPlaceholder')}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => removeEnumOption(index)}
                    aria-label={t('metadata.select.removeOption')}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Positions Field Specific */}
          {type === 'positions' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="metadata-positions-status-label-de">
                  {`${t('metadata.positions.statusLabel')} (DE)`}
                </Label>
                <Input
                  id="metadata-positions-status-label-de"
                  value={statusLabel}
                  onChange={(e) => setStatusLabel(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="metadata-positions-status-label-en">
                  {`${t('metadata.positions.statusLabel')} (EN)`}
                </Label>
                <Input
                  id="metadata-positions-status-label-en"
                  value={statusLabelEn}
                  onChange={(e) => setStatusLabelEn(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Status Field Specific */}
          {type === 'status' && (
            <div className="space-y-5 rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CircleDot className="size-4" />
                {t('metadata.status.title')}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('metadata.status.statusOptionsDescription')}
              </p>

              {renderColorVariants('green', 'greenLabel', greenVariants)}

              <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/20 p-3">
                <Checkbox
                  id="metadata-enable-yellow"
                  checked={enableYellow}
                  onCheckedChange={(checked) => setEnableYellow(Boolean(checked))}
                />
                <Label htmlFor="metadata-enable-yellow">{t('metadata.status.enableYellow')}</Label>
              </div>

              {enableYellow && <>{renderColorVariants('yellow', 'yellowLabel', yellowVariants)}</>}

              {renderColorVariants('red', 'redLabel', redVariants)}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('metadata.status.selectIcon')}</Label>
                  <Select
                    value={statusIcon}
                    onValueChange={(value) => setStatusIcon(value as StatusIcon)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusIconOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            {opt.icon ? <opt.icon className="size-4" /> : null}
                            {t(opt.labelKey)}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="metadata-group-indicator"
                    checked={isGroupIndicator}
                    onCheckedChange={(checked) => setIsGroupIndicator(Boolean(checked))}
                    disabled={isGroupIndicatorDisabled}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="metadata-group-indicator">
                      {t('metadata.status.isGroupIndicator')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('metadata.status.isGroupIndicatorDescription')}
                    </p>
                  </div>
                </div>
                {isGroupIndicatorDisabled && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <AlertTriangle className="size-3" />
                    {t('metadata.status.isGroupIndicatorAlreadyExists', {
                      fieldName: existingGroupIndicatorName ?? '',
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {type === 'relation-display' && (
            <div className="space-y-4 rounded-lg border border-border p-4">
              <div className="space-y-1">
                <div className="text-sm font-semibold">{t('metadata.relationDisplay.title')}</div>
                <p className="text-xs text-muted-foreground">
                  {t('metadata.relationDisplay.description')}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{t('metadata.relationDisplay.variant')}</Label>
                <Select
                  value={relationDisplayVariant}
                  onValueChange={(value) =>
                    setRelationDisplayVariant(
                      value as 'ship-linked-windpark-helicopters' | 'windpark-linked-ship-medics'
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ship-linked-windpark-helicopters">
                      {t('metadata.relationDisplay.variants.shipLinkedWindparkHelicopters')}
                    </SelectItem>
                    <SelectItem value="windpark-linked-ship-medics">
                      {t('metadata.relationDisplay.variants.windparkLinkedShipMedics')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {relationDisplayVariant === 'windpark-linked-ship-medics' && (
                <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
                  <div className="space-y-1">
                    <Label>{t('metadata.relationDisplay.sourceContactFields')}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t('metadata.relationDisplay.sourceContactFieldsDescription')}
                    </p>
                  </div>

                  {availableSourceContactFields.length > 0 ? (
                    <div className="flex flex-col gap-2 md:flex-row">
                      <Select
                        value={pendingSourceContactFieldKey}
                        onValueChange={setPendingSourceContactFieldKey}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSourceContactFields.map((contactField) => (
                            <SelectItem key={contactField.key} value={contactField.key}>
                              {contactField.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" onClick={addSourceContactFieldKey}>
                        <Plus className="size-4" />
                        {t('metadata.relationDisplay.addSourceContactField')}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('metadata.relationDisplay.noSourceContactFields')}
                    </p>
                  )}

                  {sourceContactFieldKeys.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {sourceContactFieldKeys.map((contactFieldKey) => {
                        const option = availableSourceContactFields.find(
                          (entry) => entry.key === contactFieldKey
                        );
                        return (
                          <Badge
                            key={contactFieldKey}
                            variant="outline-solid"
                            className="flex items-center gap-1.5 px-2 py-1"
                          >
                            <span>{option?.label ?? contactFieldKey}</span>
                            <button
                              type="button"
                              className="rounded-sm text-muted-foreground hover:text-foreground"
                              onClick={() => removeSourceContactFieldKey(contactFieldKey)}
                              aria-label={t('metadata.relationDisplay.removeSourceContactField')}
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('metadata.relationDisplay.noSelectedSourceContactFields')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Permissions */}
          <div className="space-y-3">
            <div>
              <Label>{t('metadata.permissions.title')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('metadata.permissions.description')}
              </p>
            </div>
            <div className="grid gap-4">
              {renderPermissionTable(t('metadata.permissions.gmnGroups'), gmnGroups)}
              {renderPermissionTable(t('metadata.permissions.customerGroups'), customerGroups)}
              {renderPermissionTable(
                t('metadata.permissions.heliProviderGroups'),
                heliProviderGroups
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} type="button">
            {isEditing ? t('common.save') : t('metadata.builder.createField')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
